import type { AppConfig } from './config';
import { PARTITION_KEYS, MAP_FIELDS_IN_SERIES, defaultConfig } from './config';


export function getKind(v: any): string {
  if (v === null) return 'null';
  if (Array.isArray(v)) return 'array';
  if (v instanceof Map) return 'map';
  return typeof v; // 'object' | 'number' | 'string' | ...
}

type MigrationFn = (oldValue: any, defaultValue: any) => any;
const COMMON_MIGRATIONS: Record<string, MigrationFn> = {
  test(oldValue, defaultValue) {
    if (typeof oldValue === 'number' && getKind(defaultValue) === 'object') {
      return {
        ...deepClone(defaultValue),
        value: oldValue,
      };
    }
    return oldValue;
  },
};

export function reconcileShapeWithDefaultsInPlace(
  target: any,
  defaults: any,
  prefix = '',
) {
  if (!isPlainObject(target) || !isPlainObject(defaults)) return;

  for (const key of Object.keys(defaults)) {
    const path = prefix ? `${prefix}.${key}` : key;
    const dv = defaults[key];
    const hasKey = Object.prototype.hasOwnProperty.call(target, key);

    if (!hasKey) continue;

    const tv = target[key];

    const custom = COMMON_MIGRATIONS[path];
    if (custom) {
      target[key] = custom(tv, dv);
      continue;
    }

    const defKind = getKind(dv);
    const curKind = getKind(tv);

    // If default is null, treat this field as intentionally nullable/flexible.
    // Keep loaded value shape (object/array/etc.) instead of forcing null.
    if (defKind === 'null') {
      continue;
    }

    if (isPlainObject(dv) && isPlainObject(tv)) {
      reconcileShapeWithDefaultsInPlace(tv, dv, path);
      continue;
    }

    if (defKind !== curKind) {
      target[key] = deepClone(dv);
    }
  }
}



type AnyObj = Record<string, any>;
//CONFIG_VERSION

const hasWindow = typeof window !== 'undefined';
const hasLocalStorage = hasWindow && typeof window.localStorage !== 'undefined';

export function isElectron() {
  try {
    return !!(hasWindow && (window as any).electron && (window as any).electron.ipcRenderer);
  } catch {
    return false;
  }
}

/** ===== Storage Driver ===== */
export interface StorageDriver {
  name: 'electron' | 'web' | 'memory';
  readCommon(): Promise<AnyObj>;
  writeCommon(payload: AnyObj): Promise<void>;
  resetCommon(): Promise<void>;
  readSeries(): Promise<AnyObj>;
  writeSeries(payload: AnyObj): Promise<void>;
  resetSeries(): Promise<void>;
}

/** Electron Driver */
function getIpcWrapper() {
  try { return (window as any)?.electron?.ipcRenderer ?? null; } catch { return null; }
}
export const ElectronDriver: StorageDriver = {
  name: 'electron',
  async readCommon() {
    const api = getIpcWrapper();
    if (!api) throw new Error('No IPC');
    if (typeof api.loadCommon === 'function') return (await api.loadCommon()) ?? {};
    if (typeof api.invoke === 'function') return (await api.invoke('load-common')) ?? {};
    throw new Error('No loadCommon/invoke');
  },
  async writeCommon(payload) {
    const api = getIpcWrapper();
    if (!api) throw new Error('No IPC');
    if (typeof api.saveCommon === 'function') return void (await api.saveCommon(payload));
    if (typeof api.invoke === 'function') return void (await api.invoke('save-common', payload));
    throw new Error('No saveCommon/invoke');
  },
  async resetCommon() {
    const api = getIpcWrapper();
    if (!api) throw new Error('No IPC');
    if (typeof api.resetCommonFile === 'function') return void (await api.resetCommonFile());
    if (typeof api.invoke === 'function') return void (await api.invoke('reset-common-file'));
    throw new Error('No resetCommonFile/invoke');
  },
  async readSeries() {
    const api = getIpcWrapper();
    if (!api) throw new Error('No IPC');
    if (typeof api.loadSeries === 'function') return (await api.loadSeries()) ?? {};
    if (typeof api.invoke === 'function') return (await api.invoke('load-series')) ?? {};
    throw new Error('No loadSeries/invoke');
  },
  async writeSeries(payload) {
    const api = getIpcWrapper();
    if (!api) throw new Error('No IPC');
    if (typeof api.saveSeries === 'function') return void (await api.saveSeries(payload));
    if (typeof api.invoke === 'function') return void (await api.invoke('save-series', payload));
    throw new Error('No saveSeries/invoke');
  },
  async resetSeries() {
    const api = getIpcWrapper();
    if (!api) throw new Error('No IPC');
    if (typeof api.resetSeriesFile === 'function') return void (await api.resetSeriesFile());
    if (typeof api.invoke === 'function') return void (await api.invoke('reset-series-file'));
    throw new Error('No resetSeriesFile/invoke');
  },
};

/** Web Driver(LocalStorage) */
function lsGet(key: string) {
  if (!hasLocalStorage) return null;
  try { return window.localStorage.getItem(key); } catch { return null; }
}
function lsSet(key: string, value: string) {
  if (!hasLocalStorage) return;
  try { window.localStorage.setItem(key, value); } catch {}
}
function lsRemove(key: string) {
  if (!hasLocalStorage) return;
  try { window.localStorage.removeItem(key); } catch {}
}
const COMMON_STORAGE_KEY = 'appCommon';
const SERIES_STORAGE_KEY = 'appSeries';

export const WebDriver: StorageDriver = {
  name: 'web',
  async readCommon() {
    const raw = lsGet(COMMON_STORAGE_KEY);
    if (!raw) return {};
    try { return JSON.parse(raw); } catch { return {}; }
  },
  async writeCommon(payload) { lsSet(COMMON_STORAGE_KEY, JSON.stringify(payload)); },
  async resetCommon() { lsRemove(COMMON_STORAGE_KEY); },

  async readSeries() {
    const raw = lsGet(SERIES_STORAGE_KEY);
    if (!raw) return {};
    try { return JSON.parse(raw); } catch { return {}; }
  },
  async writeSeries(payload) { lsSet(SERIES_STORAGE_KEY, JSON.stringify(payload)); },
  async resetSeries() { lsRemove(SERIES_STORAGE_KEY); },
};

/** Memory Driver */
const _mem: { common?: AnyObj; series?: AnyObj } = {};
export const MemoryDriver: StorageDriver = {
  name: 'memory',
  async readCommon() { return _mem.common ?? {}; },
  async writeCommon(payload) { _mem.common = payload ?? {}; },
  async resetCommon() { _mem.common = {}; },
  async readSeries() { return _mem.series ?? {}; },
  async writeSeries(payload) { _mem.series = payload ?? {}; },
  async resetSeries() { _mem.series = {}; },
};

export function chooseInitialDriver(): StorageDriver {
  if (isElectron()) return ElectronDriver;
  if (hasLocalStorage) return WebDriver;
  return MemoryDriver;
}

export function pick<T extends object, K extends readonly (keyof T)[]>(obj: T, keys: K) {
  const out: Partial<T> = {};
  keys.forEach((k) => { if (Object.prototype.hasOwnProperty.call(obj, k)) (out as any)[k as string] = (obj as any)[k as string]; });
  return out as Pick<T, K[number]>;
}
export const isPlainObject = (v: any) => v != null && typeof v === 'object' && !Array.isArray(v) && !(v instanceof Map);
export function deepClone(v: any): any {
  if (v === null || v === undefined) return v;
  if (Array.isArray(v)) return v.map((x) => deepClone(x));
  if (v instanceof Map) return new Map(v);
  if (isPlainObject(v)) { const o: AnyObj = {}; for (const k of Object.keys(v)) o[k] = deepClone(v[k]); return o; }
  return v;
}
export function fillMissingKeysInPlace(target: any, defaults: any) {
  if (!isPlainObject(defaults) || !isPlainObject(target)) return;
  for (const k of Object.keys(defaults)) {
    if (!Object.prototype.hasOwnProperty.call(target, k)) target[k] = deepClone(defaults[k]);
    else {
      const dv = defaults[k], tv = target[k];
      if (isPlainObject(dv) && isPlainObject(tv)) fillMissingKeysInPlace(tv, dv);
    }
  }
}
export function serializeSeriesPart(part: Partial<AppConfig>): AnyObj {
  const out: AnyObj = { ...part };
  for (const k of MAP_FIELDS_IN_SERIES) {
    const v = (out as any)[k];
    if (v && Array.isArray(v)) (out as any)[k] = v.map((item: any) => (item instanceof Map ? Array.from(item.entries()) : item));
  }
  return out;
}
export function hydrateSeriesPart(part: AnyObj): AnyObj {
  const out: AnyObj = { ...part };
  if (Array.isArray(out.isr_data)) out.isr_data = out.isr_data.map((elem: any) => (Array.isArray(elem) ? new Map(elem) : elem));
  return out;
}
export function splitCommonForSave(cfg: AppConfig) {
  const globalSave = pick(cfg, PARTITION_KEYS.global);
  return { ...globalSave };
}
export function splitSeriesForSave(cfg: AppConfig) {
  const seriesSave = serializeSeriesPart(pick(cfg, PARTITION_KEYS.series));
  return { series: seriesSave };
}
export { defaultConfig };
